import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

function Cart() {
  const navigate = useNavigate();
  const { cart, loading, updateQuantity, removeFromCart } = useCart();
  const { firebaseUser } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleQuantityChange = async (itemId, newQuantity) => {
    try {
      setError(null);
      if (newQuantity > 0) {
        await updateQuantity(itemId, newQuantity);
      }
    } catch (error) {
      setError('Failed to update quantity');
      console.error('Error updating quantity:', error);
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      setError(null);
      await removeFromCart(itemId);
    } catch (error) {
      setError('Failed to remove item');
      console.error('Error removing item:', error);
    }
  };

  const handleCheckout = async () => {
    try {
      setError(null);
      setCheckoutLoading(true);
      
      const token = await firebaseUser.getIdToken();
      const orderItems = cart.items.map(item => {
        const variant = item.product.variants.find(
          v => v.color === item.variant.color && v.size === item.variant.size
        );
        return {
          product: item.product._id,
          variant: {
            color: item.variant.color,
            size: item.variant.size
          },
          quantity: item.quantity,
          price: variant.price
        };
      });

      await axios.post(
        API_ENDPOINTS.orders,
        { items: orderItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate('/orders');
    } catch (error) {
      setError('Failed to create order');
      console.error('Error creating order:', error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
        <button
          onClick={() => navigate('/products')}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  const total = cart.items.reduce((sum, item) => {
    const variant = item.product.variants.find(
      v => v.color === item.variant.color && v.size === item.variant.size
    );
    return sum + (variant.price * item.quantity);
  }, 0);

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="divide-y divide-gray-200">
          {cart.items.map((item) => {
            const variant = item.product.variants.find(
              v => v.color === item.variant.color && v.size === item.variant.size
            );
            
            return (
              <div key={item._id} className="p-6 flex items-center">
                <img
                  src={item.product.image}
                  alt={item.product.name}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                
                <div className="ml-6 flex-1">
                  <h3 className="text-lg font-semibold">{item.product.name}</h3>
                  <p className="text-gray-600">
                    {item.variant.color} - {item.variant.size}
                  </p>
                  <p className="text-lg font-bold mt-2">₹{variant.price.toLocaleString('en-IN')}</p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center border rounded-lg">
                    <button
                      onClick={() => handleQuantityChange(item._id, item.quantity - 1)}
                      className="px-3 py-1 text-gray-600 hover:bg-gray-100"
                      disabled={checkoutLoading}
                    >
                      -
                    </button>
                    <span className="px-3 py-1">{item.quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(item._id, item.quantity + 1)}
                      className="px-3 py-1 text-gray-600 hover:bg-gray-100"
                      disabled={checkoutLoading}
                    >
                      +
                    </button>
                  </div>
                  
                  <button
                    onClick={() => handleRemoveItem(item._id)}
                    className="text-red-600 hover:text-red-800"
                    disabled={checkoutLoading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="p-6 bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-semibold">Total:</span>
            <span className="text-2xl font-bold">₹{total.toLocaleString('en-IN')}</span>
          </div>
          
          <button
            onClick={handleCheckout}
            disabled={checkoutLoading}
            className={`w-full py-3 px-6 rounded-lg font-semibold ${
              checkoutLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {checkoutLoading ? 'Processing...' : 'Proceed to Checkout'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Cart; 